import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { apiConfig, ADMIN_ROLE, validateConfig } from '../config/authConfig';

interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: AccountInfo | null;
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate configuration on mount
  useEffect(() => {
    if (!validateConfig()) {
      setError("Azure Entra ID configuration is incomplete"); // For debugging. TODO: Remove this.
      setLoading(false);
    }
  }, []);

  // Check if user has admin role
  const isAdmin = React.useMemo(() => {
    if (!account?.idTokenClaims) return false;
    const roles = (account.idTokenClaims as any)?.roles || [];
    return roles.includes(ADMIN_ROLE);
  }, [account]);

  const getAccessToken = async (): Promise<string | null> => {
    if (!account) return null;

    const request: SilentRequest = {
      scopes: apiConfig.scopes,
      account: account,
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fallback to interaction when silent call fails
        try {
          const response = await instance.acquireTokenPopup(request);
          setAccessToken(response.accessToken);
          return response.accessToken;
        } catch (popupError) {
          console.error('Failed to acquire token via popup:', popupError);
          setError('Failed to acquire access token');
          return null;
        }
      } else {
        console.error('Failed to acquire token silently:', error);
        setError('Failed to acquire access token');
        return null;
      }
    }
  };

  const login = async () => {
    try {
      setError(null);
      setLoading(true);
      
      await instance.loginPopup({
        scopes: apiConfig.scopes,
        prompt: "select_account"
      });
      
      // Token will be acquired after successful login
    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAccessToken(null);
    setError(null);
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin
    });
  };

  // Acquire token when account changes
  useEffect(() => {
    if (account && inProgress === "none") {
      getAccessToken().finally(() => {
        setLoading(false);
      });
    } else if (!account && inProgress === "none") {
      setLoading(false);
    }
  }, [account, inProgress]);

  const value: AuthContextType = {
    isAuthenticated: !!account,
    isAdmin,
    user: account,
    accessToken,
    login,
    logout,
    getAccessToken,
    loading: loading || inProgress !== "none",
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};